import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleGoogleCallback } from '../../api/auth';
import useAuth from '../../hooks/useAuth';

/**
 * Google OAuth callback handler component
 * @returns {JSX.Element} Callback handling component
 */
const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  
  useEffect(() => {
    const token = searchParams.get('token');
    
    const handleCallback = async () => {
      try {
        if (!token) {
          setError('Authentication failed. No token received.');
          return;
        }
        
        // Handle the Google callback with token
        const success = handleGoogleCallback(token);
        
        if (success) {
          // Refresh user data
          await refreshUser();
          
          // Redirect to home page or intended destination
          navigate('/', { replace: true });
        } else {
          setError('Failed to authenticate. Please try again.');
        }
      } catch (err) {
        console.error('Google callback error:', err);
        setError('An unexpected error occurred. Please try again.');
      }
    };
    
    handleCallback();
  }, [navigate, refreshUser, searchParams]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full bg-white shadow-md rounded-lg p-8">
        <div className="text-center">
          {error ? (
            <>
              <svg 
                className="mx-auto h-12 w-12 text-red-500"
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
              <h2 className="mt-2 text-lg font-medium text-gray-900">Authentication Failed</h2>
              <p className="mt-1 text-sm text-gray-500">{error}</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Return to Login
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <h2 className="mt-2 text-lg font-medium text-gray-900">Completing Authentication</h2>
              <p className="mt-1 text-sm text-gray-500">Please wait while we log you in...</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleCallback;